import { useQuery, useQueryClient } from "@tanstack/react-query";
import { workflowService } from "@/services/breeze/WorkflowService";
import { Workflow } from "@/types/breeze/Workflow";
import { getTriggerLabel } from "@/utils/workflow";
import { BasicTable } from "@/components/ui/BasicTable";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import DropdownMenu from "@/components/ui/DropdownMenu";
import {
  Edit,
  Play,
  Trash,
  History,
  Plus,
  Grid3X3,
  List,
  Search,
} from "lucide-react";
import Modal from "@/components/ui/Modal";
import WorkflowJobHistory from "@/components/workflow/WorkflowJobHistory";
import { WorkflowPreview } from "@/components/workflow/WorkflowPreview";
import WelcomeView from "@/components/ui/WelcomeView";
import { useState, useEffect, useMemo } from "react";
import PageTemplate from "@/components/page-template/PageTemplate";
import {
  Button,
  Flex,
  Card,
  Text,
  Box,
  TextField,
  IconButton,
} from "@radix-ui/themes";
import { useAuth } from "@/providers/AuthProvider";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorScreen from "@/components/ui/ErrorCard";
import TriggerWorkflowDialog from "@/components/workflow/TriggerWorkflowDialog";

const WORKFLOW_VIEW_STORAGE_KEY = "anythink-workflow-view-preference";

const WorkflowsPage = () => {
  const { currentTenant, safeNavigate } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [isTriggerWorkflowDialogOpen, setIsTriggerWorkflowDialogOpen] =
    useState(false);
  const [jobHistoryModalOpen, setJobHistoryModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null,
  );

  // Initialize view mode from localStorage
  const [viewMode, setViewMode] = useState<"table" | "grid">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(WORKFLOW_VIEW_STORAGE_KEY);
      return stored === "grid" || stored === "table" ? stored : "table";
    }
    return "table";
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [groupMode, setGroupMode] = useState<"any" | "all">("any");

  // Force refetch of workflows list when page mounts/navigates back
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["workflows"] });
  }, [queryClient]);

  // Persist view mode changes to localStorage
  useEffect(() => {
    localStorage.setItem(WORKFLOW_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const {
    data: workflows,
    isLoading,
    isFetching,
    error,
  } = useQuery<Workflow[]>({
    queryKey: ["workflows"],
    queryFn: () => workflowService.getAllWorkflows(),
    refetchOnWindowFocus: true,
  });

  const allGroups = useMemo(() => {
    const seen = new Set<string>();
    (workflows ?? []).forEach((w) => {
      if (w.group) seen.add(w.group);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [workflows]);

  // Filter workflows based on search + selected groups (AND/OR mode).
  // A workflow has at most one group string, so AND vs OR only differs when
  // the user picks 2+ chips: ANY shows workflows in any selected group; ALL
  // restricts to one chip (since a single group can't match multiple).
  const filteredWorkflows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const list = workflows ?? [];
    return list.filter((workflow) => {
      const matchesSearch =
        workflow.name.toLowerCase().includes(q) ||
        workflow.description?.toLowerCase().includes(q);
      if (!matchesSearch) return false;
      if (selectedGroups.size === 0) return true;
      if (groupMode === "all" && selectedGroups.size > 1) return false;
      return workflow.group ? selectedGroups.has(workflow.group) : false;
    });
  }, [workflows, searchQuery, selectedGroups, groupMode]);

  const toggleGroup = (g: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const handleDeleteWorkflow = async (workflow: Workflow) => {
    const confirmed = await confirm({
      title: "Delete Workflow",
      message: `Are you sure you want to delete the workflow "${workflow.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      await workflowService.deleteWorkflow(workflow.id);
      queryClient.invalidateQueries({
        queryKey: ["workflows"],
      });
    }
  };

  if (isLoading || (isFetching && (!workflows || workflows.length === 0))) {
    return <LoadingScreen fullPage={false} />;
  }

  if (error) {
    return <ErrorScreen error={error.message} />;
  }

  // Show welcome screen if no workflows exist
  if (!isLoading && (!workflows || workflows.length === 0)) {
    return (
      <PageTemplate title="Workflows">
        <WelcomeView
          title="Welcome to Workflows"
          description="Automate your business processes with powerful workflows. Create custom automation sequences that can be triggered by events, schedules, or manually."
          quickStartSteps={[
            { text: "Click 'New Workflow' to create your first workflow" },
            { text: "Choose a trigger (Event, Timed, or Manual)" },
            { text: "Add steps to define your automation sequence" },
            { text: "Test and enable your workflow" },
          ]}
          primaryAction={{
            text: "Create Your First Workflow",
            icon: <Plus className="w-4 h-4" />,
            onClick: () =>
              safeNavigate(`/org/${currentTenant?.id}/workflows/create`),
          }}
          secondaryAction={{
            text: "Learn About Workflows",
            url: "https://docs.anythink.cloud/workflows",
          }}
        />
      </PageTemplate>
    );
  }

  const renderGridView = () => (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: "1rem",
      }}
    >
      {filteredWorkflows.map((workflow) => (
        <Card key={workflow.id} style={{ padding: "1rem" }}>
          <Flex direction="column" gap="3">
            <Flex justify="between" align="center">
              <Text size="3" weight="medium">
                {workflow.name}
              </Text>
              <Flex gap="1">
                {workflow.triggers.some((t) => t.enabled) && (
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={() => {
                      setSelectedWorkflow(workflow);
                      setIsTriggerWorkflowDialogOpen(true);
                    }}
                    title="Trigger Workflow"
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() =>
                    safeNavigate(
                      `/org/${currentTenant?.id}/workflows/${workflow.id}`,
                    )
                  }
                  title="Edit Workflow"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => {
                    setSelectedWorkflow(workflow);
                    setJobHistoryModalOpen(true);
                  }}
                  title="View Job History"
                >
                  <History className="w-4 h-4" />
                </Button>
                <DropdownMenu
                  className="w-4 h-4"
                  items={[
                    {
                      label: "Delete",
                      icon: <Trash className="w-4 h-4" />,
                      onClick: () => handleDeleteWorkflow(workflow),
                      className: "text-red-600",
                    },
                  ]}
                />
              </Flex>
            </Flex>

            {workflow.description && (
              <Text size="2" color="gray">
                {workflow.description}
              </Text>
            )}

            {workflow.group && (
              <Box
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "var(--gray-3)",
                  color: "var(--gray-12)",
                  fontSize: 11,
                  alignSelf: "flex-start",
                }}
              >
                {workflow.group}
              </Box>
            )}

            <WorkflowPreview workflow={workflow} width={240} height={120} />

            <Flex justify="between" align="center">
              <Text size="1" color="gray">
                {workflow.triggers
                  .map((t) => getTriggerLabel(t.type))
                  .join(", ")}
              </Text>
              <Box
                style={{
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  background: workflow.enabled
                    ? "var(--green-3)"
                    : "var(--gray-3)",
                  color: workflow.enabled
                    ? "var(--green-11)"
                    : "var(--gray-11)",
                }}
              >
                {workflow.enabled ? "Enabled" : "Disabled"}
              </Box>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Box>
  );

  return (
    <PageTemplate title="Workflows">
      {/* Header with Search and Actions */}
      <Flex gap="4" mb="4" wrap="wrap">
        <Box style={{ flexGrow: 1 }}>
          <TextField.Root
            placeholder="Search workflows by name or description..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
          >
            <TextField.Slot>
              <Search className="h-4 w-4" />
            </TextField.Slot>
          </TextField.Root>
        </Box>

        <Flex gap="2" align="center">
          <IconButton
            variant={viewMode === "table" ? "solid" : "soft"}
            onClick={() => setViewMode("table")}
            title="Table View"
          >
            <List className="w-4 h-4" />
          </IconButton>
          <IconButton
            variant={viewMode === "grid" ? "solid" : "soft"}
            onClick={() => setViewMode("grid")}
            title="Grid View"
          >
            <Grid3X3 className="w-4 h-4" />
          </IconButton>
        </Flex>

        <Button
          onClick={() =>
            safeNavigate(`/org/${currentTenant?.id}/workflows/create`)
          }
          variant="solid"
          size="2"
        >
          <Plus className="w-4 h-4" />
          New Workflow
        </Button>
      </Flex>

      {allGroups.length > 0 && (
        <Flex gap="2" align="center" wrap="wrap" mb="3">
          <Text size="1" color="gray" style={{ marginRight: 4 }}>Groups</Text>
          {allGroups.map((g) => {
            const active = selectedGroups.has(g);
            return (
              <Button
                key={g}
                type="button"
                size="1"
                variant={active ? "solid" : "soft"}
                color={active ? undefined : "gray"}
                onClick={() => toggleGroup(g)}
              >
                {g}
              </Button>
            );
          })}
          {selectedGroups.size > 1 && (
            <Flex align="center" gap="1" ml="2">
              <Text size="1" color="gray">Match</Text>
              <Button
                type="button"
                size="1"
                variant={groupMode === "any" ? "solid" : "soft"}
                color={groupMode === "any" ? undefined : "gray"}
                onClick={() => setGroupMode("any")}
              >
                Any
              </Button>
              <Button
                type="button"
                size="1"
                variant={groupMode === "all" ? "solid" : "soft"}
                color={groupMode === "all" ? undefined : "gray"}
                onClick={() => setGroupMode("all")}
              >
                All
              </Button>
            </Flex>
          )}
          {selectedGroups.size > 0 && (
            <Button
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              onClick={() => setSelectedGroups(new Set())}
            >
              Clear
            </Button>
          )}
        </Flex>
      )}

      {viewMode === "table" ? (
        <BasicTable
          columns={[
            {
              header: "Name",
              accessor: "name",
            },
            {
              header: "Description",
              accessor: "description",
            },
            {
              header: "Group",
              accessor: "group",
              cellRenderer: (workflow) =>
                workflow.group ? (
                  <Box
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "var(--gray-3)",
                      color: "var(--gray-12)",
                      fontSize: 12,
                    }}
                  >
                    {workflow.group}
                  </Box>
                ) : (
                  <Text size="1" color="gray">—</Text>
                ),
            },
            {
              header: "Triggers",
              accessor: "triggers",
              cellRenderer: (workflow) =>
                workflow.triggers
                  .map((t) => getTriggerLabel(t.type))
                  .join(", "),
            },
            {
              header: "Status",
              accessor: "enabled",
              cellRenderer: (workflow) => (
                <span
                  className={`px-2 py-1 text-xs font-medium rounded ${
                    workflow.enabled
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {workflow.enabled ? "Enabled" : "Disabled"}
                </span>
              ),
            },
            {
              header: "Actions",
              maxWidth: 200,
              cellRenderer: (workflow) => (
                <Flex gap="2" align="center" wrap="nowrap">
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={() => {
                      setSelectedWorkflow(workflow);
                      setIsTriggerWorkflowDialogOpen(true);
                    }}
                    title={
                      !workflow.triggers.some((t) => t.enabled)
                        ? "No enabled triggers"
                        : "Trigger Workflow"
                    }
                    disabled={!workflow.triggers.some((t) => t.enabled)}
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={() =>
                      safeNavigate(
                        `/org/${currentTenant?.id}/workflows/${workflow.id}`,
                      )
                    }
                    title="Edit Workflow"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={() => {
                      setSelectedWorkflow(workflow);
                      setJobHistoryModalOpen(true);
                    }}
                    title="View Job History"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  <DropdownMenu
                    className="w-4 h-4"
                    items={[
                      {
                        label: "Delete",
                        icon: <Trash className="w-4 h-4" />,
                        onClick: () => handleDeleteWorkflow(workflow),
                        className: "text-red-600",
                      },
                    ]}
                  />
                </Flex>
              ),
            },
          ]}
          data={filteredWorkflows}
        />
      ) : (
        renderGridView()
      )}

      <Modal
        isOpen={jobHistoryModalOpen}
        onClose={() => setJobHistoryModalOpen(false)}
        title="Workflow Job History"
        description="View and analyze workflow execution history"
        size="full"
        className="h-[90vh]"
      >
        <div className="h-full overflow-hidden">
          <WorkflowJobHistory workflowId={selectedWorkflow?.id ?? 0} />
        </div>
      </Modal>

      <TriggerWorkflowDialog
        workflow={selectedWorkflow}
        isOpen={isTriggerWorkflowDialogOpen}
        onSuccess={() => setIsTriggerWorkflowDialogOpen(false)}
        onClose={() => setIsTriggerWorkflowDialogOpen(false)}
      />
    </PageTemplate>
  );
};

export default WorkflowsPage;
